import { Animation } from './animation';

/* @flow */
import { BaseRenderer } from './base.renderer';
import { ChartModel } from './chart.model';
import { getTheme, SCALE_COLOR, SCALE_NUMBER } from './constants';
import { scale } from './utils/array.util';
import { getChartMaxValue } from './utils/chart.utils';

export class MarkRenderer extends BaseRenderer {
    drawPosition: number;
    chartMax: number;
    drawFrom: number;
    drawTo: number;
    position: number;
    show: boolean;
    constructor (chart: ChartModel, width: number, height: number) {
        super(chart, width, height);
        this.drawFrom = this.chart.selectStart;
        this.drawTo = this.chart.selectEnd;
        this.position = 0;
        const _max = this.chart.getMaxSelectionValue();
        this.chartMax = getChartMaxValue(_max, SCALE_NUMBER);
    }

    render () {
        this.clear();
        if (this.show) {
            const { height, width } = this.canvas;
            const step = width / ((this.drawTo - 1) - this.drawFrom);
            if (this.chart.columns[0].type !== 'bar') {
                this.context.strokeStyle = SCALE_COLOR;
                this.context.beginPath();
                this.context.lineWidth = 1;
                this.context.moveTo(Math.round(this.drawPosition) - 0.5, 0);
                this.context.lineTo(Math.round(this.drawPosition) - 0.5, height);
                this.context.stroke();
                for (let j = 0; j < this.chart.columns.length; j++) {
                    const column = this.chart.columns[j];
                    if (!column._disabled && column.type === 'line') {
                        this.context.strokeStyle = column.color;
                        this.context.lineWidth = 2;
                        this.context.fillStyle = getTheme() === 'day' ? '#ffffff' : '#242f3e';
                        this.context.beginPath();
                        let y = 0;
                        if (this.animations.position && this.position !== this.animations.position.position) {
                            const from = Math.floor(this.drawPosition / step) + this.drawFrom;
                            const yDelta = column.data[from] - column.data[from + 1];
                            const xDelta = this.drawPosition - ((from - this.drawFrom) * step);
                            const pos = (yDelta / step) * xDelta;
                            y = scale(column.data[from] - pos, height, this.chart.yScaled ? column.max : this.chartMax);
                        } else {
                            y = scale(column.data[this.position], height, this.chart.yScaled ? column.max : this.chartMax);
                        }
                        this.context.arc(this.drawPosition - 0.5, y, 5, 0, 2 * Math.PI);
                        this.context.fill();
                        this.context.stroke();
                    }
                }
            } else {
                this.context.fillStyle = getTheme() === 'day' ? 'rgba(256,256,256,0.5)' : 'rgba(0,0,0,0.5)';
                this.context.fillRect(0, 0, Math.round(this.drawPosition), height);
                this.context.fillRect(Math.round(this.drawPosition + step), 0, width - Math.round(this.drawPosition + step), height);
            }
        }
    }

    draw () {
        if (this.progressAnimation) {
            if (this.animations.chartMax) {
                const { animation } = this.animations.chartMax;
                this.chartMax = animation.getValue();
                if (animation.finished()) {
                    delete this.animations.chartMax;
                }
            }
            if (this.animations.position) {
                const { animation } = this.animations.position;
                this.drawPosition = animation.getValue();
                if (animation.finished()) {
                    this.position = this.animations.position.position;
                    delete this.animations.position;
                }
            }
            this.progressAnimation = !!(
                this.animations.chartMax ||
                this.animations.toggleColumn ||
                this.animations.position);
            this.render();
        }
        return this.canvas;
    }

    update (changes: any) {
        this.progressAnimation = true;
        if (changes.toggleColumn !== undefined) {
            const max = this.chart.getMaxSelectionValue();
            const chartMax = getChartMaxValue(max, SCALE_NUMBER);
            changes.chartMax = chartMax;
        }
        if (changes.move !== undefined) {
            this.show = false;
            this.drawFrom = this.chart.selectStart;
            this.drawTo = this.chart.selectEnd;
            const max = this.chart.getMaxSelectionValue();
            this.chartMax = getChartMaxValue(max, SCALE_NUMBER);
        }
        if (changes.position !== undefined) {
            this.show = true;
            const { width } = this.canvas;
            const chartStep = width / ((this.drawTo - 1) - this.drawFrom);
            changes.drawPosition = (changes.position - this.drawFrom) * chartStep;
            if (this.drawPosition !== changes.drawPosition) {
                this.animations.position = {
                    animation: new Animation(this.drawPosition, changes.drawPosition, 1),
                    position: changes.position
                };
            }
        }
        if (changes.chartMax !== undefined) {
            this.animations.chartMax = {
                animation: new Animation(this.chartMax, changes.chartMax, 1) };
        }
    }
}
